import { create } from 'zustand';
import type { AudioClip } from '@/types/timeline.ts';
import type { UndoOptions } from './undoStore.ts';
import { useProjectStore } from './projectStore.ts';
import { useUndoStore } from './undoStore.ts';
import { isReadOnly } from './guards.ts';
import { getBlob, stashBlob, hasBlob, cacheBuffer, getBuffer, ensureDecoded, revokeBlobUrl } from '@/previs/audioBlobStore.ts';
import { encodeAudioBufferToWav } from '@/audio/audioProcessor.ts';
import { capabilities } from '@/capabilities.ts';

export interface PendingRecordedClip {
  clipId: number;
  duration: number;
  peaks: Float32Array;
  label: string;
  lane: number;
}

interface AudioState {
  audioClips: AudioClip[];
  /** Latest embedded-recording result not yet claimed by RecordPanel — set by the
   *  recordingComplete bridge handler, cleared once a mounted RecordPanel stages it.
   *  Covers the case where RecordPanel is unmounted when Unity's message arrives. */
  pendingRecordedClip: PendingRecordedClip | null;

  processAudioClips: () => Promise<void>;
  addAudioClip: (clip: AudioClip, opts?: UndoOptions) => void;
  removeAudioClip: (id: number) => void;
  updateAudioClip: (id: number, updates: Partial<AudioClip>, opts?: UndoOptions) => void;
  splitAudioClip: (id: number, splitTime: number, opts?: UndoOptions) => { leftId: number; rightId: number };
  deleteClipsForShot: (shotId: number) => void;
  cloneClipsForShot: (sourceShotId: number, newShotId: number) => void;
  setPendingRecordedClip: (clip: PendingRecordedClip | null) => void;
  clearPendingRecordedClip: () => void;
}

/**
 * Re-attach audioBuffer + peaks from the module-level cache for any clip
 * that has lost them (e.g. after undo/redo restore).
 */
function rehydrateBuffers(clips: AudioClip[]): AudioClip[] {
  let changed = false;
  const result = clips.map(c => {
    if (c.audioBuffer) return c;
    const cached = getBuffer(c.id);
    if (!cached) return c;
    changed = true;
    return { ...c, audioBuffer: cached.audioBuffer, peaks: cached.peaks };
  });
  return changed ? result : clips;
}

export const useAudioStore = create<AudioState>((set, get) => ({
  audioClips: [],
  pendingRecordedClip: null,

  processAudioClips: async () => {
    const { audioClips } = get();
    if (audioClips.length === 0) return;

    // Decode clips that have blobs but no audioBuffer yet
    const needsDecode = audioClips.filter(c => !c.audioBuffer && hasBlob(c.id));
    const noBlob = audioClips.filter(c => !c.audioBuffer && !hasBlob(c.id));

    // DIAG: log decode status to native console
    if (capabilities.bridge) {
      const diagMsg = `processAudioClips: ${audioClips.length} total, ${needsDecode.length} need decode (have blob), ${noBlob.length} missing blob (ids: ${noBlob.map(c => c.id).join(',')})`;
      import('@/bridge/embeddedBridge.ts').then(m => m.enqueueSendToNative('diagLog', { msg: diagMsg }));
    }

    if (needsDecode.length === 0) return;

    const results = await Promise.all(
      needsDecode.map(async (clip) => {
        try {
          const decoded = await ensureDecoded(clip.id);
          if (!decoded) return null;
          return {
            id: clip.id,
            audioBuffer: decoded.audioBuffer,
            peaks: decoded.peaks,
            duration: decoded.audioBuffer.duration,
          };
        } catch (e) {
          // DIAG: route decode errors to native console (console.warn doesn't show in Xcode)
          if (capabilities.bridge) {
            import('@/bridge/embeddedBridge.ts').then(m => m.enqueueSendToNative('diagLog', { msg: `processAudioClips: DECODE FAILED clip ${clip.id}: ${e}` }));
          }
          return null;
        }
      })
    );

    const decodedMap = new Map(
      results.filter((r): r is NonNullable<typeof r> => r !== null).map(r => [r.id, r])
    );

    // DIAG: log decode results
    if (capabilities.bridge) {
      import('@/bridge/embeddedBridge.ts').then(m => m.enqueueSendToNative('diagLog', { msg: `processAudioClips: decoded ${decodedMap.size}/${needsDecode.length} clips` }));
    }

    if (decodedMap.size === 0) return;

    set(state => ({
      audioClips: state.audioClips.map(existing => {
        const decoded = decodedMap.get(existing.id);
        if (!decoded) return existing;
        return {
          ...existing,
          audioBuffer: decoded.audioBuffer,
          peaks: decoded.peaks,
          // Preserve trimmed duration from SQLite; only fall back to buffer duration
          // when the clip has no stored duration (e.g. legacy data).
          duration: existing.duration > 0 ? existing.duration : decoded.duration,
        };
      }),
    }));
  },

  addAudioClip: (clip, opts) => {

    if (isReadOnly()) return;

    // Stash audioBuffer in module-level cache so it survives undo/redo
    if (clip.audioBuffer && clip.peaks) {
      cacheBuffer(clip.id, clip.audioBuffer, clip.peaks);
    }

    if (opts?.pushUndo !== false) useUndoStore.getState().pushSnapshot();
    set(state => ({ audioClips: [...state.audioClips, clip] }));

    // Send audio binary to Unity for persistence
    if (capabilities.bridge) {
      import('@/bridge/audioSyncBridge.ts').then(m => m.sendAudioBlobToUnity(clip.id));
    }
  },

  removeAudioClip: (id) => {

    if (isReadOnly()) return;

    useUndoStore.getState().pushSnapshot();
    set(state => ({ audioClips: state.audioClips.filter(c => c.id !== id) }));
    // Blobs persist — undo might bring the clip back
    // But revoke the blob URL to free browser resources (raw bytes still in blobMap)
    revokeBlobUrl(id);
    // Clear selection if this clip was selected
    if (useProjectStore.getState().selectedAudioClipId === id) {
      useProjectStore.setState({ selectedAudioClipId: null });
    }
  },

  updateAudioClip: (id, updates, opts) => {

    if (isReadOnly()) return;

    // If a new audioBuffer is being set, stash it in the cache
    if (updates.audioBuffer && updates.peaks) {
      cacheBuffer(id, updates.audioBuffer, updates.peaks);
    }

    if (opts?.pushUndo !== false) useUndoStore.getState().pushSnapshot();
    set(state => ({
      audioClips: state.audioClips.map(c => {
        if (c.id !== id) return c;
        const merged = { ...c, ...updates };
        // Guard: never drop a valid audioBuffer via partial updates
        if (!merged.audioBuffer && c.audioBuffer) {
          merged.audioBuffer = c.audioBuffer;
        }
        // Belt-and-suspenders: if audioBuffer is STILL null, try the cache
        if (!merged.audioBuffer) {
          const cached = getBuffer(id);
          if (cached) {
            merged.audioBuffer = cached.audioBuffer;
            merged.peaks = cached.peaks;
          }
        }
        return merged;
      }),
    }));
  },

  splitAudioClip: (id, splitTime, opts) => {

    if (isReadOnly()) return { leftId: id, rightId: id };

    if (opts?.pushUndo !== false) useUndoStore.getState().pushSnapshot();
    const { audioClips } = get();
    const clip = audioClips.find(c => c.id === id);
    if (!clip) return { leftId: id, rightId: id };

    const clipEnd = clip.shotOffset + clip.duration;
    if (splitTime <= clip.shotOffset || splitTime >= clipEnd) return { leftId: id, rightId: id };

    const leftDuration = splitTime - clip.shotOffset;
    const rightDuration = clipEnd - splitTime;
    const leftId = useProjectStore.getState()._allocId(2);
    const rightId = leftId + 1;

    const leftClip: AudioClip = {
      ...clip,
      id: leftId,
      duration: leftDuration,
    };
    const rightClip: AudioClip = {
      ...clip,
      id: rightId,
      shotOffset: splitTime,
      duration: rightDuration,
      bufferOffset: (clip.bufferOffset ?? 0) + leftDuration,
    };

    // Stash audioBuffer for both children (they share the parent's buffer)
    if (clip.audioBuffer && clip.peaks) {
      cacheBuffer(leftId, clip.audioBuffer, clip.peaks);
      cacheBuffer(rightId, clip.audioBuffer, clip.peaks);
    }

    // Stash raw blobs for children from parent's blob.
    // Fallback: if blob was somehow lost but decoded buffer is cached,
    // re-encode to WAV so children can still be saved. This changes format
    // (original may have been MP3/OGG) but preserves audio content.
    let raw = getBlob(id);
    if (!raw && clip.audioBuffer) {
      raw = encodeAudioBufferToWav(clip.audioBuffer);
    }
    if (raw) {
      stashBlob(leftId, raw);
      stashBlob(rightId, raw);
    } else {
      console.warn(`[audioStore] Split clip ${id}: no raw bytes available — children may lose audio on save`);
    }

    set(state => ({
      audioClips: [
        ...state.audioClips.filter(c => c.id !== id),
        leftClip,
        rightClip,
      ],
    }));
    // Don't remove parent's blob or buffer cache — undo might restore the parent clip

    // Send both new clips' audio binaries to Unity for persistence
    if (capabilities.bridge) {
      import('@/bridge/audioSyncBridge.ts').then(m => {
        m.sendAudioBlobToUnity(leftId);
        m.sendAudioBlobToUnity(rightId);
      });
    }

    return { leftId, rightId };
  },

  deleteClipsForShot: (shotId) => {
    const clips = get().audioClips.filter(c => c.shotId !== shotId);
    set({ audioClips: clips });
  },

  cloneClipsForShot: (sourceShotId, newShotId) => {
    const clips = get().audioClips;
    let nextId = Math.max(0, ...clips.map(c => c.id)) + 1;
    const clones = clips
      .filter(c => c.shotId === sourceShotId)
      .map(c => ({ ...c, id: nextId++, shotId: newShotId }));
    if (clones.length > 0) {
      set({ audioClips: [...clips, ...clones] });
    }
  },

  setPendingRecordedClip: (clip) => set({ pendingRecordedClip: clip }),
  clearPendingRecordedClip: () => set({ pendingRecordedClip: null }),
}));

// ── Undo/redo rehydration subscriber ─────────────────────────────────
// When audioClips changes (especially after undo/redo restore), check
// for any clips with null audioBuffer and restore from the cache.
useAudioStore.subscribe((state, prevState) => {
  if (state.audioClips === prevState.audioClips) return;
  const needsRehydration = state.audioClips.some(c => !c.audioBuffer && getBuffer(c.id));
  if (needsRehydration) {
    const rehydrated = rehydrateBuffers(state.audioClips);
    if (rehydrated !== state.audioClips) {
      useAudioStore.setState({ audioClips: rehydrated });
    }
  }
});
